import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './Settings.css';

/** YYYY-MM-DD strings, inclusive; empty if invalid */
function datesBetweenInclusive(startYmd, endYmd) {
  if (!startYmd || !endYmd || startYmd > endYmd) return [];
  const partsStart = startYmd.split('-').map(Number);
  const partsEnd = endYmd.split('-').map(Number);
  if (partsStart.length !== 3 || partsEnd.length !== 3) return [];
  const [sy, sm, sd] = partsStart;
  const [ey, em, ed] = partsEnd;
  const out = [];
  const cursor = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const day = String(cursor.getDate()).padStart(2, '0');
    out.push(`${y}-${m}-${day}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

function Settings() {
  const [settings, setSettings] = useState({
    supportEmail: '',
    supportPhone: ''
  });
  const [offers, setOffers] = useState({
    offerDates: [],
    fullOfferCounter: 0,
    halfOfferCounter: 0,
    isFullOfferExceeded: false,
    isHalfOfferExceeded: false,
    // New fields
    isLaunchingOfferEnabled: false,
    isWeekendOfferEnabled: false,
    weekendOfferPercentage: 0,
    isCustomOfferEnabled: false,
    customOfferStartDate: '',
    customOfferEndDate: '',
    customOfferDefaultPercentage: 0,
    customOfferOverrides: []
  });
  const [customOverrideDate, setCustomOverrideDate] = useState('');
  const [customOverridePercentage, setCustomOverridePercentage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offerSaving, setOfferSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [offerError, setOfferError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [offerSuccessMessage, setOfferSuccessMessage] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchOffers();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      const settingsRef = doc(db, 'settings', 'general');
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        setSettings({
          supportEmail: data.supportEmail || '',
          supportPhone: data.supportPhone || ''
        });
      } else {
        // Initialize with default values if document doesn't exist
        setSettings({
          supportEmail: 'support@ulloorstyles.com',
          supportPhone: '+91 9876543210'
        });
      }
    } catch (err) {
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOffers = async () => {
    try {
      const offersRef = doc(db, 'offers', 'general');
      const offersSnap = await getDoc(offersRef);
      
      if (offersSnap.exists()) {
        const data = offersSnap.data();
        const rawOverrides = data.customOfferOverrides;
        const normalizedOverrides = Array.isArray(rawOverrides)
          ? rawOverrides
              .filter((o) => o && typeof o.date === 'string')
              .map((o) => ({
                date: o.date,
                percentage: Number(o.percentage) || 0
              }))
              .sort((a, b) => a.date.localeCompare(b.date))
          : [];
        setOffers({
          offerDates: data.offerDates || [],
          fullOfferCounter: data.fullOfferCounter || 0,
          halfOfferCounter: data.halfOfferCounter || 0,
          isFullOfferExceeded: data.isFullOfferExceeded || false,
          isHalfOfferExceeded: data.isHalfOfferExceeded || false
          ,
          // Load new fields safely
          isLaunchingOfferEnabled: data.isLaunchingOfferEnabled || false,
          isWeekendOfferEnabled: data.isWeekendOfferEnabled || false,
          weekendOfferPercentage: data.weekendOfferPercentage != null ? data.weekendOfferPercentage : 0,
          isCustomOfferEnabled: !!data.isCustomOfferEnabled,
          customOfferStartDate: data.customOfferStartDate || '',
          customOfferEndDate: data.customOfferEndDate || '',
          customOfferDefaultPercentage:
            data.customOfferDefaultPercentage != null ? data.customOfferDefaultPercentage : 0,
          customOfferOverrides: normalizedOverrides
        });
      } else {
        // Initialize with default values
        setOffers({
          offerDates: [],
          fullOfferCounter: 20,
          halfOfferCounter: 30,
          isFullOfferExceeded: false,
          isHalfOfferExceeded: false
          ,
          isLaunchingOfferEnabled: false,
          isWeekendOfferEnabled: false,
          weekendOfferPercentage: 0,
          isCustomOfferEnabled: false,
          customOfferStartDate: '',
          customOfferEndDate: '',
          customOfferDefaultPercentage: 0,
          customOfferOverrides: []
        });
      }
    } catch (err) {
      setOfferError('Failed to load offer settings. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const handleOfferChange = (field, value) => {
    setOffers(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear messages when user starts typing
    if (offerError) setOfferError('');
    if (offerSuccessMessage) setOfferSuccessMessage('');
  };

  const handleLaunchingOfferToggle = (isEnabled) => {
    if (!isEnabled) {
      // Turning OFF the launching offer
      const confirmed = window.confirm(
        'Are you sure you want to turn off the launching offer? It will reset your selected dates and counter values.'
      );
      
      if (confirmed) {
        setOffers(prev => ({
          ...prev,
          isLaunchingOfferEnabled: false,
          offerDates: [],
          fullOfferCounter: 0,
          halfOfferCounter: 0,
          isFullOfferExceeded: false,
          isHalfOfferExceeded: false
        }));
        setOfferSuccessMessage('Launching offer has been turned off and settings have been reset.');
        setTimeout(() => setOfferSuccessMessage(''), 3000);
      }
      // If not confirmed, do nothing (keep toggle on)
    } else {
      // Turning ON the launching offer
      setOffers(prev => ({
        ...prev,
        isLaunchingOfferEnabled: true,
        isFullOfferExceeded: false,
        isHalfOfferExceeded: false
      }));
      setOfferSuccessMessage('Launching offer has been enabled. Please configure dates and counters.');
      setTimeout(() => setOfferSuccessMessage(''), 3000);
    }
  };

  const handleDateAdd = () => {
    const dateInput = document.getElementById('offer-date-input');
    const selectedDate = dateInput.value;
    
    if (!selectedDate) {
      setOfferError('Please select a date');
      return;
    }

    const dateObj = new Date(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (dateObj < today) {
      setOfferError('Please select a future date');
      return;
    }

    const dateString = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    setOffers(prev => {
      const currentDates = prev.offerDates;
      
      if (currentDates.includes(dateString)) {
        setOfferError('This date is already selected');
        return prev;
      }
      
      if (currentDates.length >= 2) {
        setOfferError('You can only select up to 2 dates');
        return prev;
      }
      
      // Clear any previous errors
      setOfferError('');
      
      return {
        ...prev,
        offerDates: [...currentDates, dateString].sort()
      };
    });
    
    // Clear the input
    dateInput.value = '';
  };

  const handleDateRemove = (dateToRemove) => {
    setOffers(prev => ({
      ...prev,
      offerDates: prev.offerDates.filter(date => date !== dateToRemove)
    }));
  };

  const customOfferPreviewDates = useMemo(
    () => datesBetweenInclusive(offers.customOfferStartDate, offers.customOfferEndDate),
    [offers.customOfferStartDate, offers.customOfferEndDate]
  );

  const overrideMap = useMemo(() => {
    const m = new Map();
    (offers.customOfferOverrides || []).forEach((o) => m.set(o.date, o.percentage));
    return m;
  }, [offers.customOfferOverrides]);

  const handleCustomOverrideAdd = () => {
    const start = offers.customOfferStartDate;
    const end = offers.customOfferEndDate;
    if (!start || !end || start > end) {
      setOfferError('Set a valid custom offer date range first');
      return;
    }
    const date = customOverrideDate;
    if (!date) {
      setOfferError('Select a date for the override');
      return;
    }
    if (date < start || date > end) {
      setOfferError('Override date must be within the custom offer range');
      return;
    }
    const pct = Number(customOverridePercentage);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setOfferError('Override percentage must be between 0 and 100');
      return;
    }
    if (offers.customOfferOverrides.some((o) => o.date === date)) {
      setOfferError('That date already has a custom percentage');
      return;
    }
    setOfferError('');
    setOffers((prev) => ({
      ...prev,
      customOfferOverrides: [...prev.customOfferOverrides, { date, percentage: pct }].sort((a, b) =>
        a.date.localeCompare(b.date)
      )
    }));
    setCustomOverrideDate('');
    setCustomOverridePercentage('');
  };

  const handleCustomOverrideRemove = (dateStr) => {
    setOffers((prev) => ({
      ...prev,
      customOfferOverrides: prev.customOfferOverrides.filter((o) => o.date !== dateStr)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!settings.supportEmail || !settings.supportPhone) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      
      const settingsRef = doc(db, 'settings', 'general');
      await setDoc(settingsRef, {
        supportEmail: settings.supportEmail,
        supportPhone: settings.supportPhone,
        updatedAt: new Date()
      }, { merge: true });
      
      setSuccessMessage('Settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (err) {
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleOfferSave = async (e) => {
    e.preventDefault();
    
    // Only validate dates and counters if launching offer is enabled
    if (offers.isLaunchingOfferEnabled) {
      if (offers.offerDates.length !== 2) {
        setOfferError('Please select exactly 2 offer dates');
        return;
      }

      if (offers.fullOfferCounter <= 0 || offers.halfOfferCounter <= 0) {
        setOfferError('Offer counters must be greater than 0');
        return;
      }
    }

    // Validate weekend percentage if weekend offers enabled
    if (offers.isWeekendOfferEnabled) {
      const pct = Number(offers.weekendOfferPercentage);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        setOfferError('Weekend offer percentage must be a number between 0 and 100');
        return;
      }
    }

    if (offers.isCustomOfferEnabled) {
      const start = offers.customOfferStartDate;
      const end = offers.customOfferEndDate;
      if (!start || !end) {
        setOfferError('Custom offer requires both start and end dates');
        return;
      }
      if (start > end) {
        setOfferError('Custom offer start date must be on or before end date');
        return;
      }
      const def = Number(offers.customOfferDefaultPercentage);
      if (Number.isNaN(def) || def < 0 || def > 100) {
        setOfferError('Custom offer default percentage must be between 0 and 100');
        return;
      }
      for (const o of offers.customOfferOverrides) {
        const p = Number(o.percentage);
        if (Number.isNaN(p) || p < 0 || p > 100) {
          setOfferError('Each override percentage must be between 0 and 100');
          return;
        }
        if (o.date < start || o.date > end) {
          setOfferError('All override dates must fall within the custom offer range');
          return;
        }
      }
    }

    try {
      setOfferSaving(true);
      setOfferError('');
      setOfferSuccessMessage('');
      
      const offersRef = doc(db, 'offers', 'general');
      await setDoc(offersRef, {
        offerDates: offers.offerDates,
        fullOfferCounter: parseInt(offers.fullOfferCounter),
        halfOfferCounter: parseInt(offers.halfOfferCounter),
        isFullOfferExceeded: offers.isFullOfferExceeded,
        isHalfOfferExceeded: offers.isHalfOfferExceeded,
        // Persist new fields
        isLaunchingOfferEnabled: !!offers.isLaunchingOfferEnabled,
        isWeekendOfferEnabled: !!offers.isWeekendOfferEnabled,
        weekendOfferPercentage: Number(offers.weekendOfferPercentage) || 0,
        isCustomOfferEnabled: !!offers.isCustomOfferEnabled,
        customOfferStartDate: offers.customOfferStartDate || '',
        customOfferEndDate: offers.customOfferEndDate || '',
        customOfferDefaultPercentage: Number(offers.customOfferDefaultPercentage) || 0,
        customOfferOverrides: (offers.customOfferOverrides || []).map((o) => ({
          date: o.date,
          percentage: Number(o.percentage) || 0
        })),
        updatedAt: new Date()
      }, { merge: true });
      
      setOfferSuccessMessage('Offer settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setOfferSuccessMessage('');
      }, 3000);
      
    } catch (err) {
      setOfferError('Failed to save offer settings. Please try again.');
    } finally {
      setOfferSaving(false);
    }
  };

  const handleResetOffers = async () => {
    if (!window.confirm('Are you sure you want to reset all offer counters? This action cannot be undone.')) {
      return;
    }

    try {
      setResetting(true);
      setOfferError('');
      setOfferSuccessMessage('');
      
      const offersRef = doc(db, 'offers', 'general');
      await setDoc(offersRef, {
        fullOfferCounter: 0,
        halfOfferCounter: 0,
        isFullOfferExceeded: false,
        isHalfOfferExceeded: false,
        updatedAt: new Date()
      }, { merge: true });
      
      // Update local state
      setOffers(prev => ({
        ...prev,
        fullOfferCounter: 0,
        halfOfferCounter: 0,
        isFullOfferExceeded: false,
        isHalfOfferExceeded: false
      }));
      
      setOfferSuccessMessage('Offer counters reset successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setOfferSuccessMessage('');
      }, 3000);
      
    } catch (err) {
      setOfferError('Failed to reset offer counters. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <h2>Settings/Offers</h2>
        <p>Manage application settings and support information</p>
      </div>

      <div className="settings-container">
        <div className="settings-section">
          <h3>Support Contact Information</h3>
          <p className="section-description">
            Update the support contact details that will be displayed to users
          </p>
          
          <form onSubmit={handleSave} className="settings-form">
            <div className="form-group">
              <label htmlFor="supportEmail">Support Email</label>
              <input
                type="email"
                id="supportEmail"
                value={settings.supportEmail}
                onChange={(e) => handleInputChange('supportEmail', e.target.value)}
                placeholder="support@ulloorstyles.com"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="supportPhone">Support Phone</label>
              <input
                type="tel"
                id="supportPhone"
                value={settings.supportPhone}
                onChange={(e) => handleInputChange('supportPhone', e.target.value)}
                placeholder="+91 9876543210"
                required
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
            
            <button 
              type="submit" 
              className="save-btn"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </form>
        </div>

        <div className="settings-section">
          <h3>Offer Settings</h3>
          <p className="section-description">
            Configure offer days and availability counters
          </p>
          
          <form onSubmit={handleOfferSave} className="settings-form">
            <div className="form-group">
              <label>Offer Dates (Select exactly 2 dates)</label>
              <div className="date-selector-container">
                <div className="date-input-row">
                  <input
                    type="date"
                    id="offer-date-input"
                    className="date-input"
                    min={new Date().toISOString().split('T')[0]}
                    disabled={!offers.isLaunchingOfferEnabled}
                  />
                  <button
                    type="button"
                    onClick={handleDateAdd}
                    className="add-date-btn"
                    disabled={offers.offerDates.length >= 2 || !offers.isLaunchingOfferEnabled}
                  >
                    Add Date
                  </button>
                </div>
                
                <div className="selected-dates">
                  {offers.offerDates.map((date, index) => (
                    <div key={date} className="selected-date-item">
                      <span className="date-text">
                        {new Date(date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDateRemove(date)}
                        className="remove-date-btn"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <small className="form-help">
                Selected dates: {offers.offerDates.length}/2
                {offers.offerDates.length === 0 && ' - Please select 2 future dates for offers'}
                {!offers.isLaunchingOfferEnabled && ' (Enable launching offer to configure dates)'}
              </small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fullOfferCounter">Full Offer Counter</label>
                <input
                  type="number"
                  id="fullOfferCounter"
                  value={offers.fullOfferCounter}
                  onChange={(e) => handleOfferChange('fullOfferCounter', e.target.value)}
                  placeholder="20"
                  min="1"
                  disabled={!offers.isLaunchingOfferEnabled}
                  required={offers.isLaunchingOfferEnabled}
                />
                <small className="form-help">
                  Maximum number of full offers available
                  {!offers.isLaunchingOfferEnabled && ' (Enable launching offer to configure)'}
                </small>
              </div>
              
              <div className="form-group">
                <label htmlFor="halfOfferCounter">Half Offer Counter</label>
                <input
                  type="number"
                  id="halfOfferCounter"
                  value={offers.halfOfferCounter}
                  onChange={(e) => handleOfferChange('halfOfferCounter', e.target.value)}
                  placeholder="50"
                  min="1"
                  disabled={!offers.isLaunchingOfferEnabled}
                  required={offers.isLaunchingOfferEnabled}
                />
                <small className="form-help">
                  Maximum number of half offers available
                  {!offers.isLaunchingOfferEnabled && ' (Enable launching offer to configure)'}
                </small>
              </div>
            </div>

            {/* Launching Offer Controls - Prominent Card */}
            <div className="launching-offer-card">
              <div className="launching-offer-header">
                <h4>Launching Offer</h4>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={!!offers.isLaunchingOfferEnabled}
                    onChange={(e) => handleLaunchingOfferToggle(e.target.checked)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
              <p className="launching-offer-desc">
                Enable the main launching offer to give priority discounts. This is the primary offer — make it visible to customers.
                {!offers.isLaunchingOfferEnabled && (
                  <span className="warning-text"> (Currently disabled - dates and counters are reset)</span>
                )}
              </p>
            </div>

            {/* Weekend Offer Controls */}
            <div className="form-row">
              <div className="form-group">
                <label>Weekend Offer</label>
                <div className="switch-with-label">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={!!offers.isWeekendOfferEnabled}
                      onChange={(e) => handleOfferChange('isWeekendOfferEnabled', e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="switch-label">{offers.isWeekendOfferEnabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                <small className="form-help">Enable percentage-based weekend offers</small>
              </div>

              <div className="form-group">
                <label htmlFor="weekendOfferPercentage">Weekend Offer Percentage</label>
                <input
                  type="number"
                  id="weekendOfferPercentage"
                  value={offers.weekendOfferPercentage}
                  onChange={(e) => handleOfferChange('weekendOfferPercentage', e.target.value)}
                  placeholder="10"
                  min="0"
                  max="100"
                  disabled={!offers.isWeekendOfferEnabled}
                />
                <small className="form-help">Enter percentage (0-100). Field is disabled when weekend offer is off.</small>
              </div>
            </div>

            {/* Custom date-range offer with optional per-day overrides */}
            <div className="custom-offer-card">
              <div className="custom-offer-header">
                <h4>Custom offer (date range)</h4>
                <div className="switch-with-label">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={!!offers.isCustomOfferEnabled}
                      onChange={(e) => handleOfferChange('isCustomOfferEnabled', e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="switch-label">{offers.isCustomOfferEnabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              <p className="custom-offer-desc">
                Pick a start and end date, set one discount percentage for the whole range, then optionally add
                different percentages for specific days inside that range.
              </p>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customOfferStartDate">Start date</label>
                  <input
                    type="date"
                    id="customOfferStartDate"
                    className="date-input"
                    value={offers.customOfferStartDate}
                    onChange={(e) => handleOfferChange('customOfferStartDate', e.target.value)}
                    disabled={!offers.isCustomOfferEnabled}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="customOfferEndDate">End date</label>
                  <input
                    type="date"
                    id="customOfferEndDate"
                    className="date-input"
                    value={offers.customOfferEndDate}
                    min={offers.customOfferStartDate || undefined}
                    onChange={(e) => handleOfferChange('customOfferEndDate', e.target.value)}
                    disabled={!offers.isCustomOfferEnabled}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="customOfferDefaultPercentage">Default percentage (entire range)</label>
                <input
                  type="number"
                  id="customOfferDefaultPercentage"
                  value={offers.customOfferDefaultPercentage}
                  onChange={(e) => handleOfferChange('customOfferDefaultPercentage', e.target.value)}
                  placeholder="e.g. 15"
                  min="0"
                  max="100"
                  disabled={!offers.isCustomOfferEnabled}
                />
                <small className="form-help">
                  Applies to every day in the range unless you add an override for that day.
                </small>
              </div>

              <div className="form-group">
                <label>Overrides for specific dates</label>
                <div className="override-add-row">
                  <input
                    type="date"
                    className="date-input"
                    aria-label="Override date"
                    value={customOverrideDate}
                    onChange={(e) => setCustomOverrideDate(e.target.value)}
                    min={offers.customOfferStartDate || undefined}
                    max={offers.customOfferEndDate || undefined}
                    disabled={
                      !offers.isCustomOfferEnabled ||
                      !offers.customOfferStartDate ||
                      !offers.customOfferEndDate ||
                      offers.customOfferStartDate > offers.customOfferEndDate
                    }
                  />
                  <input
                    type="number"
                    className="override-pct-input"
                    placeholder="%"
                    min="0"
                    max="100"
                    aria-label="Override percentage"
                    value={customOverridePercentage}
                    onChange={(e) => setCustomOverridePercentage(e.target.value)}
                    disabled={
                      !offers.isCustomOfferEnabled ||
                      !offers.customOfferStartDate ||
                      !offers.customOfferEndDate ||
                      offers.customOfferStartDate > offers.customOfferEndDate
                    }
                  />
                  <button
                    type="button"
                    className="add-date-btn"
                    onClick={handleCustomOverrideAdd}
                    disabled={
                      !offers.isCustomOfferEnabled ||
                      !offers.customOfferStartDate ||
                      !offers.customOfferEndDate ||
                      offers.customOfferStartDate > offers.customOfferEndDate
                    }
                  >
                    Add override
                  </button>
                </div>
                <small className="form-help">
                  Choose a day inside the range and a percentage; it replaces the default for that day only.
                </small>
              </div>

              {offers.customOfferOverrides.length > 0 && (
                <ul className="override-list">
                  {offers.customOfferOverrides.map((o) => (
                    <li key={o.date} className="override-list-item">
                      <span className="override-list-date">
                        {new Date(o.date + 'T12:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      <span className="override-list-pct">{o.percentage}%</span>
                      <button
                        type="button"
                        className="remove-date-btn"
                        onClick={() => handleCustomOverrideRemove(o.date)}
                        aria-label={`Remove override for ${o.date}`}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {offers.isCustomOfferEnabled &&
                offers.customOfferStartDate &&
                offers.customOfferEndDate &&
                offers.customOfferStartDate <= offers.customOfferEndDate &&
                customOfferPreviewDates.length > 0 && (
                  <div className="custom-offer-preview">
                    <span className="custom-offer-preview-title">Effective percentage by day</span>
                    <div className="custom-offer-preview-table-wrap">
                      <table className="custom-offer-preview-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customOfferPreviewDates.map((d) => {
                            const overridden = overrideMap.has(d);
                            const pct = overridden
                              ? overrideMap.get(d)
                              : Number(offers.customOfferDefaultPercentage) || 0;
                            return (
                              <tr key={d} className={overridden ? 'override-row' : ''}>
                                <td>
                                  {new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </td>
                                <td>
                                  {pct}%
                                  {overridden && <span className="override-badge">override</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </div>
            
            {offerError && <div className="error-message">{offerError}</div>}
            {offerSuccessMessage && <div className="success-message">{offerSuccessMessage}</div>}
            
            <div className="button-group">
              <button 
                type="submit" 
                className="save-btn"
                disabled={offerSaving || resetting}
              >
                {offerSaving ? 'Saving...' : 'Save Offer Settings'}
              </button>
              
              <button 
                type="button" 
                className="reset-btn"
                onClick={handleResetOffers}
                disabled={offerSaving || resetting}
              >
                {resetting ? 'Resetting...' : 'Reset Offer Counters'}
              </button>
            </div>
          </form>
        </div>

        <div className="settings-section">
          <h3>Application Links</h3>
          <p className="section-description">
            Important application links and policies
          </p>
          
          <div className="links-container">
            <div className="link-item">
              <span className="link-label">Privacy Policy</span>
              <a 
                href="https://ulloorstyles.com/privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-url"
              >
                View Privacy Policy
              </a>
            </div>
            
            <div className="link-item">
              <span className="link-label">Terms of Service</span>
              <a 
                href="https://ulloorstyles.com/terms-of-service" 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-url"
              >
                View Terms of Service
              </a>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>Application Information</h3>
          <div className="app-info">
            <div className="info-item">
              <span className="info-label">Version:</span>
              <span className="info-value">1.0.0</span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Updated:</span>
              <span className="info-value">{new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;


